import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Search, MapPin, ExternalLink, Bookmark, Send } from "lucide-react";

const SOURCES = [
  { key: "linkedin", label: "LinkedIn" },
  { key: "indeed", label: "Indeed" },
  { key: "glassdoor", label: "Glassdoor" },
  { key: "naukri", label: "Naukri" },
];

export default function JobSearch() {
  const [kw, setKw] = useState("");
  const [loc, setLoc] = useState("");
  const [selectedSources, setSelectedSources] = useState(["linkedin", "indeed", "glassdoor"]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null); // {sources, done_sources}
  const [notice, setNotice] = useState("");
  const [apifyConfigured] = useState(true);
  const activeSearch = useRef(null);

  const finish = () => {
    activeSearch.current = null;
    localStorage.removeItem("cc_active_search");
    setLoading(false);
    setProgress(null);
  };

  // Fallback: load already-saved matches for this keyword instead of waiting forever
  const loadSavedMatches = async (keyword) => {
    try {
      const r = await api.get("/jobs/matches?limit=50");
      const kwLower = (keyword || "").toLowerCase();
      const mine = (r.data || []).filter((j) => (j.search_keyword || "").toLowerCase() === kwLower);
      if (mine.length) {
        setJobs(mine);
        setNotice("Showing your saved results for this search — fresh ones also land in the Job Matches tab.");
      }
    } catch { /* ignore */ }
  };

  const pollOnce = async () => {
    const cur = activeSearch.current;
    if (!cur) return false;
    try {
      const st = await api.get(`/jobs/search/status/${cur.search_id}`);
      if (st.data.jobs?.length) setJobs(st.data.jobs); // progressive: show as each source finishes
      setProgress({ sources: st.data.sources || [], done: st.data.done_sources || [] });
      if (st.data.status === "done") { finish(); return true; }
      if (st.data.status === "failed") {
        setNotice(st.data.error || "Search failed. Please try again.");
        finish();
        return true;
      }
    } catch { /* transient blip — keep polling */ }
    return false;
  };

  const pollUntilDone = async (keyword) => {
    for (let i = 0; i < 60; i++) {
      if (!activeSearch.current) return;
      await new Promise((res) => setTimeout(res, 4000));
      if (await pollOnce()) return;
    }
    // Timed out — don't leave the user hanging: show whatever is saved
    await loadSavedMatches(keyword);
    setNotice((n) => n || "Search is taking long — showing saved results. Check Job Matches for the rest.");
    finish();
  };

  const search = async () => {
    if (!kw.trim()) return;
    setLoading(true);
    setJobs([]);
    setNotice("");
    try {
      const start = await api.post("/jobs/search/start", {
        keyword: kw, location: loc, sources: selectedSources, limit_per_source: 8,
      });
      activeSearch.current = { search_id: start.data.search_id, keyword: kw };
      localStorage.setItem("cc_active_search", JSON.stringify({ ...activeSearch.current, ts: Date.now() }));
      await pollUntilDone(kw);
    } catch (e) {
      setNotice(e.response?.data?.detail || "Search failed");
      finish();
    }
  };

  // Resume an in-progress search when returning to this page (e.g. after screen lock / tab switch)
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("cc_active_search") || "null");
      if (saved && Date.now() - saved.ts < 5 * 60 * 1000) {
        activeSearch.current = saved;
        setKw(saved.keyword);
        setLoading(true);
        pollOnce().then((done) => { if (!done) pollUntilDone(saved.keyword); });
      } else {
        localStorage.removeItem("cc_active_search");
      }
    } catch { /* ignore */ }
    const onVisible = () => { if (document.visibilityState === "visible") pollOnce(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = async (job) => {
    if (job.url) window.open(job.url, "_blank", "noopener");
    try {
      await api.post("/applications", { job_id: job.job_id, status: "Applied" });
    } catch { /* tracked already or transient */ }
  };

  return (
    <div className="space-y-6" data-testid="job-search-page">
      {!apifyConfigured && (
        <div className="jr-card border border-amber-500/40 bg-amber-500/5 text-amber-200 text-sm" data-testid="apify-warning">
          <strong>Demo mode:</strong> APIFY_TOKEN is not set. Showing realistic demo jobs. Add your token in <code>backend/.env</code> and restart to pull live LinkedIn / Indeed / Glassdoor jobs.
        </div>
      )}

      <div className="jr-card">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-3.5" />
            <input data-testid="search-keyword" value={kw} onChange={(e) => setKw(e.target.value)}
              placeholder="Job title or keyword" className="jr-input !pl-10" />
          </div>
          <div className="flex-1 relative">
            <MapPin className="w-4 h-4 text-zinc-400 absolute left-3 top-3.5" />
            <input data-testid="search-location" value={loc} onChange={(e) => setLoc(e.target.value)}
              placeholder="Location (optional)" className="jr-input !pl-10" />
          </div>
          <button onClick={search} disabled={loading} className="jr-btn-primary" data-testid="search-btn">
            {loading ? "Scraping live jobs..." : "Search Jobs"}
          </button>
        </div>
        {loading && (
          <div className="mt-3 text-xs text-zinc-400 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="search-loading-hint">
            <span>Pulling real listings via Apify:</span>
            {(progress?.sources?.length ? progress.sources : selectedSources).map((s) => (
              <span key={s} className={progress?.done?.includes(s) ? "text-emerald-300" : "text-zinc-500 jr-pulse"} data-testid={`source-progress-${s}`}>
                {progress?.done?.includes(s) ? "✓ " : "… "}{s}
              </span>
            ))}
            <span className="text-zinc-600">— results appear below as each source finishes</span>
          </div>
        )}
        {notice && (
          <div className="mt-3 text-xs text-amber-300" data-testid="search-notice">{notice}</div>
        )}
        <div className="mt-3 flex gap-2 flex-wrap">
          {SOURCES.map((s) => {
            const on = selectedSources.includes(s.key);
            return (
              <button key={s.key} data-testid={`source-${s.key}`}
                onClick={() => setSelectedSources(on ? selectedSources.filter(x => x !== s.key) : [...selectedSources, s.key])}
                className={`jr-pill ${on ? "bg-violet-500/25 text-violet-200" : "bg-white/5 text-zinc-400"}`}>
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid gap-3" data-testid="job-list">
        {jobs.length === 0 && !loading && (
          <div className="jr-card text-center text-zinc-400">Search to discover jobs. Try &quot;Software Engineer&quot;, &quot;Data Scientist&quot;, etc.</div>
        )}
        {jobs.map((j) => (
          <div key={j.job_id} className="jr-card flex items-center gap-4" data-testid="job-card">
            <div className="w-12 h-12 rounded-lg bg-white/10 grid place-items-center text-white font-bold">
              {(j.company || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-white">{j.title}</div>
              <div className="text-xs text-zinc-400">{j.company} • {j.location} • <span className="text-violet-300">{j.source}</span></div>
              <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{j.description?.slice(0, 200)}</div>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <span className="jr-pill bg-emerald-500/15 text-emerald-300">{j.match_score}% Match</span>
              {j.salary && <span className="text-xs text-zinc-400">{j.salary}</span>}
              <div className="flex gap-2">
                {j.url && <a href={j.url} target="_blank" rel="noreferrer" className="jr-btn-ghost text-xs py-1.5"><ExternalLink className="w-3 h-3" /></a>}
                <button onClick={() => apply(j)} className="jr-btn-primary text-xs py-1.5" data-testid="apply-btn">
                  <Send className="w-3 h-3 inline mr-1" /> Apply
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
